<template>
  <div ref="root" class="dashboard-graphic" data-dashboard-charts>
    <div ref="canvas" />
  </div>
</template>

<script setup>
/* global defineProps, defineEmits */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { donutChart, hBarChart, stackedBar } from '../../../../dashboard/public/js/charts.js';
import { renderMap } from '../../../../dashboard/public/js/map.js';
import { removeTooltip } from '../../../../dashboard/public/js/tooltip.js';

const props = defineProps({
  kind: { type: String, required: true },
  options: { type: Object, required: true },
  label: { type: String, required: true },
  filter: { type: String, default: null }
});
const emit = defineEmits(['select']);
const root = ref(null);
const canvas = ref(null);
let observer;
let controller;
let width = 0;

function draw() {
  if (!canvas.value) return;
  removeTooltip(root.value);
  if (props.kind === 'map') {
    controller = renderMap(canvas.value, { ...props.options, onSelect: point => emit('select', point) });
    controller.setFilter(props.filter);
  } else if (props.kind === 'stack') {
    stackedBar(canvas.value, props.options);
  } else if (props.kind === 'donut') {
    donutChart(canvas.value, props.options);
  } else {
    hBarChart(canvas.value, props.options);
  }
  canvas.value.querySelector('svg')?.setAttribute('aria-label', props.label);
}

watch(() => [props.kind, props.options], draw, { flush: 'post' });
watch(() => props.filter, filter => controller?.setFilter(filter));
onMounted(() => {
  draw();
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(entries => {
      const next = Math.round(entries[0].contentRect.width);
      if (next !== width) { width = next; draw(); }
    });
    observer.observe(root.value);
  }
});
onBeforeUnmount(() => {
  observer?.disconnect();
  removeTooltip(root.value);
});
</script>

<style scoped>
.dashboard-graphic {
  --series-1: #3b7dd8;
  --ink: #1a1a1a;
  --ink-muted: #6b6b6b;
  --border-strong: #d0cec9;
  min-width: 0;
}
.dashboard-graphic :deep(.chart), .dashboard-graphic :deep(.map-svg) { width: 100%; display: block; }
.dashboard-graphic :deep(.chart text) { font-family: inherit; }
.dashboard-graphic :deep(.axis-label), .dashboard-graphic :deep(.axis-label-tab) { fill: var(--ink-muted); font-size: 11px; }
.dashboard-graphic :deep(.value-label) { fill: var(--ink); font-size: 12px; font-weight: 600; }
.dashboard-graphic :deep(.gridline) { stroke: #e8e6e3; stroke-width: 1; }
.dashboard-graphic :deep(.baseline) { stroke: var(--border-strong); stroke-width: 1; }
.dashboard-graphic :deep(.hit) { fill: transparent; cursor: pointer; }
.dashboard-graphic :deep(.hit:focus-visible) { outline: 2px solid #2563eb; outline-offset: 2px; }
.dashboard-graphic :deep(.donut-segment:focus-visible) { outline: 2px solid #2563eb; outline-offset: 2px; }
.dashboard-graphic :deep(.map-svg) { border-radius: 8px; background: #fafaf9; }
.dashboard-graphic :deep(.map-boundary) { fill: #f0f0ed; stroke: var(--border-strong); stroke-width: 1.25; }
.dashboard-graphic :deep(.map-scrim-outside) { fill: #fff; fill-opacity: .66; }
.dashboard-graphic :deep(.map-scrim-inside) { fill: #fff; fill-opacity: .28; }
.dashboard-graphic :deep(.map-boundary-over) { fill: none; stroke: #0f172a; stroke-width: 1.75; stroke-opacity: .55; }
.dashboard-graphic :deep(.tooltip) {
  position: fixed; z-index: 1080; max-width: min(340px, calc(100vw - 24px)); padding: 10px 12px;
  background: #fff; color: #212529; border: 1px solid #d4d7dc; border-radius: 8px;
  box-shadow: 0 4px 18px #0002; pointer-events: none; opacity: 0; font: inherit; font-size: 12px;
}
.dashboard-graphic :deep(.tooltip[data-show='true']) { opacity: 1; }
.dashboard-graphic :deep(.tt-title) { font-weight: 700; margin-bottom: 4px; }
.dashboard-graphic :deep(.tt-sub) { color: #626970; margin-bottom: 6px; }
.dashboard-graphic :deep(.tt-row) { display: flex; align-items: center; gap: 6px; }
.dashboard-graphic :deep(.tt-key), .dashboard-graphic :deep(.tt-key-dot) { width: 9px; height: 9px; flex-shrink: 0; border-radius: 50%; }
.dashboard-graphic :deep(.tt-value) { font-weight: 600; }
</style>
